import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import cx from 'classnames';
import format from 'date-fns/format';
import { handleKeyDown } from '../common/accessibility';
import { BlockTypes, BlockStatus, MIN_ZOOM, MD_MEDIA_QUERY, SIGNATURE_FONTS } from '../common/constants';
import { getTimeZoneShortName } from '../lib/DateUtils';
import type { IBlock, ISigner, ISignatureSettings, DateFormatType } from '../store/types';
import { colors } from '../theme';
import AutosizingBlock from './AutosizingBlock';
import Checkbox from './Checkbox';
import Info from './common/Info';
import Tag from './common/Tag';
import TextBox from './TextBox';
import Tooltip from './Tooltip';

const useStyles = makeStyles(() => ({
  wrapper: {
    position: 'absolute',
    display: 'flex',
    lineHeight: '1',
    alignItems: 'flex-end',
    fontFamily: 'courier',
    '&:focus': {
      outline: 'none',
      '& $signature': {
        outline: `2px solid ${colors.teal}`,
      },
    },
  },
  signatureContainer: {
    display: 'flex !important',
    alignItems: 'flex-end',
    position: 'relative',
    lineHeight: '1',
    height: '100%',
    width: '100%',
    color: colors.darkIndigo,
    '& svg': {
      position: 'absolute',
      height: '100%',
      width: '100%',
    },
  },
  signature: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    lineHeight: 0.65,
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  signatureImage: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
  backgroundDots: {
    position: 'absolute',
    display: 'flex',
    height: '100%',
    width: '100%',
    transition: 'all 0.5s',
    opacity: 1,
  },
  block: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    height: '100%',
    width: '100%',
  },
  tag: {
    position: 'absolute',
    left: -32,
    cursor: 'pointer',
  },
  blockOverflow: {
    overflow: 'hidden',
  },
  checkbox: {
    padding: 4,
  },
  requiredOutline: {
    '&:focus': {
      outline: 'none',
      '& $signature': {
        outline: `2px solid #D20004`,
      },
    },
  },
  checkboxWrapper: {
    display: 'flex',
    justifyContent: 'end',
  },
  span: {
    marginRight: '2px',
    color: `${colors.red}`,
  },
  mdScreenBlock: {
    height: '100%',
    width: '100%',
    backgroundColor: '#2DAAC0',
  },
  mdScreenBlockNotFocused: {
    backgroundColor: 'rgba(45,170,192,0.6)',
  },
}));

interface IProps {
  block: IBlock;
  zoom: number;
  clickSignature: (blockIndex: number) => void;
  updateBlock: (updates: any) => void;
  index: number;
  value?: any;
  keyIndex: number;
  signer: ISigner;
  disabled?: boolean;
  tabIndex: number;
  currentSelectedBlockIndex: number | null;
  signingExperienceComplete: boolean;
  tabToBlock: (tabIndex: number) => void;
  pageDimensions: { height?: number; width?: number };
  pageScale: number;
  signatureSettings?: ISignatureSettings;
  scrollToBlock: (blockElement: HTMLDivElement) => void;
  dateFormatPreference: DateFormatType;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const Block = (props: IProps) => {
  const screenWidthIsLessThanOrEqualToMdBreakpoint = useMediaQuery(MD_MEDIA_QUERY);
  const [dismissedTooltip, setDismissedTooltip] = React.useState(false);
  const blockRef = React.useRef<HTMLDivElement>(null);
  const { index, block, zoom, signingExperienceComplete, pageDimensions, dateFormatPreference } = props;
  const classes = useStyles();
  const { height, width } = pageDimensions;

  useEffect(() => {
    setDismissedTooltip(false);
  }, [props.currentSelectedBlockIndex]);

  useEffect(() => {
    if (props.currentSelectedBlockIndex !== null && props.currentSelectedBlockIndex === props.tabIndex) {
      props.scrollToBlock(blockRef.current!);
    } else {
      blockRef!.current!.blur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.currentSelectedBlockIndex]);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const isCheckboxDisabled = () => {
    return props.disabled && block.status !== 'Complete' ? true : props.block.status === 'Pending' && props.disabled;
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const renderTag = (index: number) => {
    if (!screenWidthIsLessThanOrEqualToMdBreakpoint || props.currentSelectedBlockIndex === props.tabIndex) {
      return (
        <div
          className={classes.tag}
          role="button"
          tabIndex={0}
          onClick={() => props.clickSignature(index)}
          onTouchEnd={() => props.clickSignature(index)}
          onKeyDown={(event) => handleKeyDown(event, () => props.clickSignature(index))}
        >
          <Tag color="teal" />
        </div>
      );
    }
    return '';
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const Dots = (props: { size: 'small' | 'large' }) => (
    <svg width="100%" height="100%">
      <defs>
        {props.size === 'small' ? (
          <pattern id="ds--dots-pattern" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
            <ellipse rx="0.5" ry="0.5" fill="#00ACC1" stroke="none" transform="translate(0.5, 0.5)" />
            <ellipse rx="0.5" ry="0.5" fill="#00ACC1" stroke="none" transform="translate(2.5,2.5)" />
          </pattern>
        ) : (
          <pattern id="ds--dots-pattern" x="0" y="2" width="8" height="8" patternUnits="userSpaceOnUse">
            <ellipse rx="1" ry="1" fill="#00ACC1" stroke="none" transform="translate(1,1)" />
            <ellipse rx="1" ry="1" fill="#00ACC1" stroke="none" transform="translate(5,5)" />
          </pattern>
        )}
      </defs>
      <rect x="0" y="0" width="100%" height="100%" fill="url(#ds--dots-pattern)" />
    </svg>
  );

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const renderBlockSvg = () => {
    if (screenWidthIsLessThanOrEqualToMdBreakpoint && props.tabIndex === props.currentSelectedBlockIndex) {
      return <div className={classes.mdScreenBlock}></div>;
    } else if (screenWidthIsLessThanOrEqualToMdBreakpoint && props.tabIndex !== props.currentSelectedBlockIndex) {
      return <div className={cx(classes.mdScreenBlock, classes.mdScreenBlockNotFocused)}></div>;
    } else {
      return <Dots size={props.zoom === MIN_ZOOM ? 'small' : 'large'} key={props.keyIndex} />;
    }
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const renderBlock = () => {
    const wrapperClass =
      block.status === BlockStatus.COMPLETE ? classes.wrapper : cx(classes.signature, classes.blockOverflow);

    switch (block.blockType) {
      case BlockTypes.FULLNAME: {
        const name = [props.signer?.firstName, props.signer?.middleName, props.signer?.lastName]
          .filter(Boolean)
          .join(' ');
        return (
          <AutosizingBlock
            width={block.width!}
            height={block.height!}
            value={name}
            pageScale={props.pageScale}
            fontFamily="courier"
            fontSize={block.fontSize || 12}
            updateBlock={props.updateBlock}
            parentRef={blockRef}
            blockType={block.blockType}
            dataSpec={`${block.blockType}-${index}`}
          />
        );
      }
      case BlockTypes.SIGNATURE:
      case BlockTypes.INITIALS:
        if (block.status === BlockStatus.PENDING) {
          return (
            <div className={classes.block}>
              {renderTag(props.tabIndex)}
              <div
                className={wrapperClass}
                role="button"
                tabIndex={0}
                onClick={() => props.clickSignature(props.tabIndex)}
                onTouchEnd={() => props.clickSignature(props.tabIndex)}
                onKeyDown={(event) => handleKeyDown(event, () => props.clickSignature(props.tabIndex))}
                style={{
                  height: `${screenWidthIsLessThanOrEqualToMdBreakpoint ? '100%' : block.height! * zoom + 'px'}`,
                  width: `${screenWidthIsLessThanOrEqualToMdBreakpoint ? '100%' : block.width! * zoom + 'px'}`,
                  cursor: 'pointer',
                }}
              >
                {renderBlockSvg()}
              </div>
            </div>
          );
        }
        if (!props.signatureSettings) return '';
        if (props.signatureSettings!.signatureType === 'text') {
          // @ts-expect-error -- This code was written prior to enforcing lint rules
          const signatureFontFamily = SIGNATURE_FONTS[props.signatureSettings?.signatureFont || 'Damion'].fontFamily;
          return (
            <div className={classes.signatureContainer} style={{ fontFamily: signatureFontFamily }}>
              <div className={classes.signature}>
                <AutosizingBlock
                  width={block.width!}
                  height={block.height!}
                  value={
                    block.blockType === BlockTypes.SIGNATURE
                      ? props.signatureSettings!.signatureValue || ''
                      : props.signatureSettings!.initialsValue || ''
                  }
                  pageScale={props.pageScale}
                  fontFamily={signatureFontFamily}
                  fontSize={block.fontSize || 12}
                  updateBlock={props.updateBlock}
                  parentRef={blockRef}
                  blockType={block.blockType}
                  dataSpec={`${block.blockType}-${index}`}
                />
              </div>
            </div>
          );
        }
        return (
          <div className={classes.signatureContainer}>
            <img
              className={classes.signatureImage}
              alt="your signed signature"
              src={
                block.blockType === BlockTypes.SIGNATURE
                  ? props.signatureSettings.signatureValue || ''
                  : props.signatureSettings.initialsValue || ''
              }
            />
          </div>
        );
      case BlockTypes.DATE_SIGNED:
      case BlockTypes.TIME_SIGNED: {
        const isDateSigned = block.blockType === BlockTypes.DATE_SIGNED;
        return (
          <AutosizingBlock
            width={block.width!}
            height={block.height!}
            value={`${format(
              block.value! as string,
              isDateSigned ? dateFormatPreference.toUpperCase() : `${dateFormatPreference.toUpperCase()}, hh:mm:ss A`
            )} ${!isDateSigned ? getTimeZoneShortName() : ''}`}
            pageScale={props.pageScale}
            fontFamily="courier"
            fontSize={block.fontSize || 12}
            updateBlock={props.updateBlock}
            parentRef={blockRef}
            blockType={block.blockType}
            dataSpec={`${block.blockType}-${index}`}
          />
        );
      }
      case BlockTypes.TEXT:
        return (
          <TextBox
            blockId={block.blockId}
            value={(block.value as string) || undefined}
            updateBlock={props.updateBlock}
            height={block.height!}
            width={block.width!}
            fontSize={block.fontSize!}
            key={index}
            signingExperienceComplete={signingExperienceComplete}
            required={!!block.required}
            focused={props.tabIndex === props.currentSelectedBlockIndex}
            tabIndex={props.tabIndex}
            tabToBlock={props.tabToBlock}
            pageDimensions={props.pageDimensions}
            pageScale={props.pageScale}
          />
        );
      case BlockTypes.CHECKBOX: {
        const checkboxDisabled = isCheckboxDisabled();
        return (
          <Checkbox
            checked={block.value!}
            onClick={() =>
              props.updateBlock({
                value: !block.value,
                status: 'Complete',
              })
            }
            disabled={checkboxDisabled}
            index={props.index}
            block={block}
            zoom={zoom}
          />
        );
      }
      default:
        return '';
    }
  };
  let wrapperClass = signingExperienceComplete ? cx(classes.wrapper, classes.requiredOutline) : classes.wrapper;
  if (block.blockType === 'Checkbox') {
    wrapperClass = cx(wrapperClass, classes.checkboxWrapper);
  }
  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const generateTooltip = (): JSX.Element | void => {
    if (props.tabIndex === props.currentSelectedBlockIndex && !block.value) {
      if (block.required) {
        return (
          <>
            <Tooltip
              closeTooltip={() => setDismissedTooltip(true)}
              zoom={props.zoom}
              visible={!dismissedTooltip}
              data-spec={'requiredTooltip'}
            >
              <span className={classes.span}>*</span>Required
            </Tooltip>
            <Info visible={block.required} toggleTooltip={() => setDismissedTooltip(false)} zoom={zoom} />
          </>
        );
      } else if (!signingExperienceComplete) {
        return (
          <Tooltip
            closeTooltip={() => setDismissedTooltip(true)}
            zoom={props.zoom}
            visible={!dismissedTooltip}
            data-spec={'optionalTooltip'}
          >
            Optional
          </Tooltip>
        );
      }
    }
  };
  const blockLeft = `${(block.x! / width!) * 100}%`;
  const blockTop = `${(block.y! / height!) * 100}%`;
  return (
    <div
      className={wrapperClass}
      style={{
        left: blockLeft,
        top: blockTop,
        height: `${(block.height! / height!) * 100}%`,
        width: `${(block.width! / width!) * 100}%`,
      }}
      id={`ds-${block.blockType}-${index}`}
      tabIndex={props.tabIndex}
      ref={blockRef}
    >
      {generateTooltip()}
      {renderBlock()}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default Block;
